import * as queryString from 'query-string';
import _ from 'underscore';

import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { VizService } from '~src/viz/services/VizService';

import { LoginUrlsWrapper } from '../../authentication/components/loginSpace/LoginUrls';
import { ExplorableItemTypeValue } from '../../spacesPlatform/models/ContentType';
import { Helpers } from '../utils/Helpers';

export function isSitePlacePath(path: string): boolean {
  const types: string[] = Object.keys(ExplorableItemTypeValue);
  return new RegExp('^(\/site\/[^\/]+)?\/(' + types.join('|') + ')s(\/)?$').test(path);
}

export function isProjectPlacePath(projectId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/projects\/' + projectId + '\/(.*)?').test(path);
}

export function isWorkbookPlacePath(workbookId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/workbooks\/' + workbookId + '\/(.*)?').test(path);
}

export function isDatasourcePlacePath(datasourceId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/datasources\/' + datasourceId + '\/(.*)?').test(path);
}

export function isFlowPlacePath(flowId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/flows\/' + flowId + '\/(.*)?').test(path);
}

export function isDataRolePlacePath(roleId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/dataRoles\/' + roleId + '\/(.*)?').test(path);
}

export function isLensPlacePath(lensId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/lenses\/' + lensId + '\/(.*)?').test(path);
}

export function getPathFromUrl(url: string): string {
  if (!url) {
    return url;
  }

  const hashPrefix = '#';
  const queryStrPrefix = '?';

  const start = url.includes(hashPrefix) ? url.indexOf(hashPrefix) + 1 : 0;
  const end = url.includes(queryStrPrefix, start) ? url.indexOf(queryStrPrefix, start) : url.length;

  return url.slice(start, end);
}

export function isCurrentUrlViz(): boolean {
  const currentUrl = WindowLocationService.getHref();
  return isVizUrl(currentUrl);
}

/**
 * Checks if given path is a valid sign-in path.
 * @param path The path component of a url, not the full url, e.g., '/signin'
 */
export function isSignInPath(path: string): boolean {
  return path === LoginUrlsWrapper.getInstance().sitePath() // e.g., 'http://hostname/#/site'
    || path.startsWith(LoginUrlsWrapper.getInstance().signinPath()); // e.g., 'http://hostname/#/signin?disableAutoSignin=yes'
}

/**
 * Checks if a url points to the root path and does not have a path component in the fragment, e.g.,
 * http://hostname:3000 will return true.
 * http://hostname:3000/#/site will return false since it points to the '/site' path in the fragment.
 * @param url Url must not be null or undefined.
 */
export function isRootPath(url: string) {
  const newUrl = new URL(url);
  return newUrl.pathname === '/' &&
    (getPathFromUrl(url) === url || getPathFromUrl(url) === '/' || getPathFromUrl(url) === '');
}

export function isVizUrl(url: string): boolean {
  const regExp = /[^/]\/(views)\/([^?#]*)/;
  const match = url.match(regExp);
  return !!(match && match[2]);
}

/**
 * Gets the viz path from a full url. For example, url:
 *
 * uri:     http://localhost/vizportal/#/views/sizetabs/auto/workgroupadmin@tsi.lan/noiceland?blah
 * returns: sizetabs/auto/workgroupadmin@tsi.lan/noiceland
 */
export function getVizPath(uri: string) {
  const regExp = /[^/]\/(views|workbooks)\/([^?#]*)/;
  const match = uri.match(regExp);
  return match && match[2] || null;
}

export function isMetricUrl(url: string): boolean {
  const regExp = /[^/]\/(metrics)\/([^?#]*)/;
  const match = url.match(regExp);
  return !!(match && match[2]);
}

export function isSearchSpaceUrl(url: string): boolean {
  const regExp = /\/search/g;
  const match = url.match(regExp);
  return !!(match && match[0]) && !isVizUrl(url);
}

export function isGrantAccessViewUrl(url: string): boolean {
  const regExp = /[^/]\/(views)\/([^?#]*)\/(grantAccess)\/(users)\/([^?#]*)/;
  const match = url.match(regExp);
  return !!(match && match[0]);
}

export function getWorkbookFromVizUrl(url: string): string {
  const path = getVizPath(url);
  const pathParts = path.split('/');
  return (pathParts && pathParts.length > 1 && pathParts[0]) || '';
}

export function isAskDataUsageAnalyticsUrl(url: string): boolean {
  const regExp = /[^/]\/(datasources)\/([^?#]*)\/(askDataUsageAnalytics)/;
  const match = url.match(regExp);
  return !!(match && match[2]);
}

export function shouldSetPreviousUrl(oldUrl: string, newUrl: string, vizService: VizService): boolean {
  const fromSearchToSearch = isSearchSpaceUrl(oldUrl) && isSearchSpaceUrl(newUrl);  // e.g., updating search term or changing tabs
  const notRedirectUrl = !vizService.isRedirectingToVizUrl(oldUrl) && !isRedirectingToMetricUrl(oldUrl);
  const redirectFromLogin = isSignInPath(getPathFromUrl(oldUrl));

  // When loading a metric detail screen as the first view, we call this function twice
  // with `oldUrl` and `newUrl` being the same. We don't want to set the previous url
  // in this case.
  const sameMetricUrl = isMetricUrl(oldUrl) && oldUrl === newUrl;

  // we add :iid and other parameters to the viz page when we load it. We don't need to keep those because
  // the we would go back to the same viz (with different parameters).
  const vizUrlsForSameWorkbook = isVizUrl(oldUrl) && isVizUrl(newUrl) &&
    (getWorkbookFromVizUrl(oldUrl) === getWorkbookFromVizUrl(newUrl));

  // add special case when data usasge analytics is opened in a separate path
  // don't reset so that it will default back to its parent datasource url
  if (isAskDataUsageAnalyticsUrl(newUrl) && newUrl === oldUrl) {
    return false;
  }

  // add a special case when the user tries to open the grant access view url and then gets redirected to the actual view url. 
  // In this scenario we should set the previous url even though the redirect is happening to the view in the same workbook. 
  // If the redirect is not happening from the grant access view url then we should look for whether the viz urls are from the same workbook.
  const setPreviousVizUrlForSameWorkbook = isGrantAccessViewUrl(oldUrl) ? vizUrlsForSameWorkbook : !vizUrlsForSameWorkbook;

  return (notRedirectUrl && !fromSearchToSearch && setPreviousVizUrlForSameWorkbook && !redirectFromLogin && !sameMetricUrl);
}

function isRedirectingToMetricUrl(url: string): boolean {
  const regexString_Redirect = '.*/redirect_to_metric/[0-9]+';
  const regExp_Redirect = new RegExp(regexString_Redirect);

  const isUrlRedirectToMetric = regExp_Redirect.test(url);
  return isUrlRedirectToMetric;
}

export function shouldResetPreviousUrl(currentPath: string, isChangingSites: boolean) {
  return isLoginInPath(currentPath) || isChangingSites;
}

export function isLoginInPath(path: string): boolean {
  return path === '/signin';
}

export function getQueryFromUrl(url: string) {
  const queryIndex = url.lastIndexOf('?');
  // so that we don't parse out the feature flags if there are no query params.
  const hashIndex = url.indexOf('#');

  let query = '';
  if (queryIndex > -1 && queryIndex > hashIndex) {
    query = url.substring(queryIndex);
  }
  // this will return an empty object if the parse goes awry.
  return queryString.parse(query);
}

/**
 * Gets the site urlName from the current url if it is the login state
 */
export function getSiteUrlNameIfOnSigninPath(path: string, searchParams: {}): string {
  if (path.startsWith('#/signin') && _.has(searchParams, 'site')) {
    return (searchParams as any).site;
  }
  return null;
}

/**
 * Checks if the given parameter is set to true in the given url.
 * First checks the query part in the fragment part of the url, and then checks the actual search part of the url,
 * and returns true if the param is found to be true in any of those.
 * e.g., https://hostname:3000/?%3AnyParam=y#/views/workbookName/viewName?:iid=1
 * @param url Url must not be null or undefined.
 * @param param Param whose boolean value is being requested.
 */
export function getBooleanParamValue(url: string, param: string): boolean {
  const search = getQueryFromUrl(url);
  if (Helpers.BooleanQueryParamRegex.test(search[param])) {
    return true;
  }

  const newUrl = new URL(url);
  const searchParams = new URLSearchParams(newUrl.search);
  return Helpers.BooleanQueryParamRegex.test(searchParams.get(param));
}
